/**
 * Visit Notes list UI: patient names as "First Last" with sentence-style casing
 * (first alphabetic character of each token uppercased, rest lowercased).
 * Handles "LAST, FIRST" combined strings from the API when split fields are missing.
 */

function capitalizeFirstAlphaRestLower(word: string): string {
  const lower = word.toLowerCase();
  const i = lower.search(/[a-z]/i);
  if (i === -1) return lower;
  return lower.slice(0, i) + lower.charAt(i).toUpperCase() + lower.slice(i + 1);
}

const ROMAN_SUFFIX = /^(I{1,3}|IV|VI{0,3}|IX|X)$/;

function normalizeToken(word: string): string {
  const w = word.trim();
  if (!w) return w;
  if (w.includes("'")) {
    return w
      .split("'")
      .map((p) => capitalizeFirstAlphaRestLower(p))
      .join("'");
  }
  const upper = w.toUpperCase();
  if (ROMAN_SUFFIX.test(upper)) {
    return upper;
  }
  return capitalizeFirstAlphaRestLower(w);
}

/** One given or family sub-string (may contain spaces, hyphens). */
export function normalizeVisitNotesNamePart(raw: string): string {
  return raw
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((t) =>
      t
        .split("-")
        .map((h) => normalizeToken(h))
        .join("-")
    )
    .join(" ");
}

function stripCommaNoise(s: string): string {
  return s.replace(/^[,\s]+|[,\s]+$/g, "").trim();
}

function trySplitLastCommaFirst(
  raw: string
): { first: string; last: string } | null {
  const idx = raw.indexOf(",");
  if (idx === -1) return null;
  const last = stripCommaNoise(raw.slice(0, idx));
  const first = stripCommaNoise(raw.slice(idx + 1));
  if (!first || !last) return null;
  return { first, last };
}

/**
 * Normalizes `first_name`, `last_name`, and `name` on a patient row from visit-notes list APIs.
 * Display order is always First Last. Comma form in `name` fills missing split fields when needed.
 */
export function normalizeVisitNotesPatientRow<
  T extends {
    first_name?: string | null;
    last_name?: string | null;
    name?: string | null;
  }
>(patient: T): T {
  let first =
    patient.first_name != null ? String(patient.first_name).trim() : "";
  let last = patient.last_name != null ? String(patient.last_name).trim() : "";
  const rawName = patient.name != null ? String(patient.name).trim() : "";

  const fromName = trySplitLastCommaFirst(rawName);
  if (fromName) {
    if (!last) last = fromName.last;
    if (!first) first = fromName.first;
  }

  const firstN = first ? normalizeVisitNotesNamePart(first) : "";
  const lastN = last ? normalizeVisitNotesNamePart(last) : "";
  const fromParts = [firstN, lastN].filter(Boolean).join(" ").trim();

  let display = fromParts;
  if (!display && rawName) {
    const comma = trySplitLastCommaFirst(rawName);
    if (comma) {
      display = `${normalizeVisitNotesNamePart(
        comma.first
      )} ${normalizeVisitNotesNamePart(comma.last)}`.trim();
    } else {
      display = normalizeVisitNotesNamePart(rawName);
    }
  }

  const nameOut = display || rawName;

  return {
    ...patient,
    first_name: firstN as T["first_name"],
    last_name: lastN as T["last_name"],
    name: nameOut as T["name"],
  };
}

/**
 * Label for v2/resident/listing (and similar) patient rows: **First Last**, title-cased.
 */
export function formatResidentListingPatientLabel(p: {
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
}): string {
  const row = normalizeVisitNotesPatientRow({
    first_name: p.first_name ?? "",
    last_name: p.last_name ?? "",
    name: (p.name ?? "").trim(),
  });
  return row.name?.trim() || (p.name ?? "").trim() || "";
}
